import {
  useListScenes,
  useUpdateScenePriorities,
  getListScenesQueryKey,
  type ScenesSceneSummaryResponse,
} from '@smarthome/api';
import { useQueryClient } from '@tanstack/react-query';
import { GripVertical, ArrowLeft, AlertCircle, Save, Info } from 'lucide-react';
import { useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import { Button, Badge, useToast } from '@/components/ui';
import { cn } from '@/components/ui/utils';

/**
 * ScenesReorderPage - Drag-and-drop or button-based priority reordering
 */
export function ScenesReorderPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const { data, isLoading, isError, refetch } = useListScenes();

  // Local state for the ordered list
  const [orderedIds, setOrderedIds] = useState<string[] | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragCounter = useRef(0);

  const scenes = useMemo(() => data?.scenes ?? [], [data?.scenes]);

  // Initialize orderedIds when scenes load
  const sortedScenes = useMemo(() => {
    // Sort by priority descending (highest priority = top)
    return [...scenes].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  }, [scenes]);

  // Use orderedIds if user has made changes, otherwise use sortedScenes order
  const displayOrder = useMemo(() => {
    if (orderedIds) {
      // Map IDs to scenes
      return orderedIds
        .map((id) => scenes.find((s) => s.id === id))
        .filter((s): s is ScenesSceneSummaryResponse => !!s);
    }
    return sortedScenes;
  }, [orderedIds, sortedScenes, scenes]);

  const hasChanges = useMemo(() => {
    if (!orderedIds) return false;
    const originalOrder = sortedScenes.map((s) => s.id);
    return JSON.stringify(orderedIds) !== JSON.stringify(originalOrder);
  }, [orderedIds, sortedScenes]);

  const { mutate: updatePriorities } = useUpdateScenePriorities({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Priorities updated', variant: 'success' });
        queryClient.invalidateQueries({ queryKey: getListScenesQueryKey() });
        navigate('/scenes');
      },
      onError: (err) => {
        setError(err instanceof Error ? err.message : 'Failed to update priorities');
        setIsSaving(false);
      },
    },
  });

  // Drag and drop handlers
  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    // Add a slight delay to allow the drag image to be set
    setTimeout(() => {
      const target = e.target as HTMLElement;
      target.style.opacity = '0.5';
    }, 0);
  }, []);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    const target = e.target as HTMLElement;
    target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
    dragCounter.current = 0;
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    dragCounter.current++;
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragOverIndex(null);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, targetIndex: number) => {
      e.preventDefault();
      const sourceIndex = draggedIndex;

      if (sourceIndex === null || sourceIndex === targetIndex) {
        setDraggedIndex(null);
        setDragOverIndex(null);
        dragCounter.current = 0;
        return;
      }

      const currentOrder = orderedIds ?? sortedScenes.map((s) => s.id ?? '').filter(Boolean);
      const newOrder = [...currentOrder];
      const [removed] = newOrder.splice(sourceIndex, 1);
      newOrder.splice(targetIndex, 0, removed!);
      setOrderedIds(newOrder);

      setDraggedIndex(null);
      setDragOverIndex(null);
      dragCounter.current = 0;
    },
    [draggedIndex, orderedIds, sortedScenes]
  );

  const handleSave = useCallback(() => {
    if (!hasChanges) return;
    setIsSaving(true);
    setError(null);

    // Convert order to priorities: top item gets highest priority
    const priorities = displayOrder.map((scene, index) => ({
      id: scene.id!,
      priority: displayOrder.length - index, // N for top, 1 for bottom
    }));

    updatePriorities({
      data: { priorities },
    });
  }, [hasChanges, displayOrder, updatePriorities]);

  const handleCancel = useCallback(() => {
    navigate('/scenes');
  }, [navigate]);

  const handleReset = useCallback(() => {
    setOrderedIds(null);
    setError(null);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Link
              to="/scenes"
              className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-500 dark:hover:bg-white/[0.04] dark:hover:text-white"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Scene Priorities
            </h1>
          </div>
          {/* Helper line */}
          <div className="mt-1 pl-10 text-sm">
            {hasChanges ? (
              <span className="text-blue-600 dark:text-blue-400">Unsaved changes</span>
            ) : (
              <span className="text-gray-400 dark:text-zinc-600">Drag and drop to reorder</span>
            )}
          </div>
        </div>

        {/* Header actions */}
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Button variant="ghost" size="sm" onClick={handleReset}>
              Reset
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!hasChanges} loading={isSaving}>
            <Save className="mr-2 h-4 w-4" />
            Save Order
          </Button>
        </div>
      </div>

      {/* Info box explaining priorities */}
      <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4 dark:border-blue-500/20 dark:bg-blue-500/5">
        <div className="flex gap-3">
          <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500" />
          <div className="space-y-2 text-sm">
            <p className="font-medium text-blue-900 dark:text-blue-300">How priorities work</p>
            <ul className="space-y-1.5 text-blue-800 dark:text-blue-300/90">
              <li>
                <strong>Higher priority = takes precedence.</strong> Scenes at the top of this list
                have higher priority numbers.
              </li>
              <li>
                <strong>Conflict resolution is per-property.</strong> When multiple active scenes
                control the same device property (e.g., brightness), the higher-priority scene's
                value is used.
              </li>
              <li>
                <strong>Non-conflicting properties combine.</strong> If one scene sets brightness
                and another sets color temperature, both values apply.
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Priority list */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
          </div>
        ) : isError ? (
          <div className="p-6 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
              <AlertCircle className="h-6 w-6 text-red-400" />
            </div>
            <p className="text-sm text-gray-600 dark:text-zinc-400">Failed to load scenes</p>
            <button
              onClick={() => refetch()}
              className="mt-3 text-sm text-blue-500 hover:text-blue-400"
            >
              Try again
            </button>
          </div>
        ) : displayOrder.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-sm text-gray-600 dark:text-zinc-400">No scenes to reorder</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-white/[0.06]">
            {/* Header row */}
            <div className="flex items-center gap-4 bg-gray-50 px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:bg-zinc-800/50 dark:text-zinc-500">
              <span className="w-8" />
              <span className="w-16 text-center">Priority</span>
              <span className="flex-1">Scene</span>
            </div>

            {displayOrder.map((scene, index) => (
              <div
                key={scene.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragEnter={(e) => handleDragEnter(e, index)}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                className={cn(
                  'flex cursor-grab items-center gap-4 px-4 py-3 transition-all active:cursor-grabbing',
                  draggedIndex === index && 'opacity-50',
                  dragOverIndex === index &&
                    draggedIndex !== index &&
                    'border-t-2 border-t-blue-500 bg-blue-50/50 dark:bg-blue-500/5',
                  dragOverIndex !== index && 'hover:bg-gray-50 dark:hover:bg-white/[0.02]'
                )}
              >
                {/* Drag handle */}
                <div className="flex h-8 w-8 items-center justify-center text-gray-400 dark:text-zinc-600">
                  <GripVertical className="h-5 w-5" />
                </div>

                {/* Priority badge - shows position clearly */}
                <div className="flex w-16 justify-center">
                  <Badge variant="info" size="md">
                    {displayOrder.length - index}
                  </Badge>
                </div>

                {/* Scene info */}
                <div className="flex flex-1 items-center gap-3">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-white">{scene.name}</div>
                    <div className="text-sm text-gray-500 dark:text-zinc-500">
                      {scene.deviceCount} device{scene.deviceCount !== 1 ? 's' : ''}
                    </div>
                  </div>
                  {scene.active && (
                    <Badge variant="info" size="sm" dot>
                      Active
                    </Badge>
                  )}
                  {!scene.enabled && (
                    <Badge variant="default" size="sm">
                      Disabled
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
